import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, MouseEvent } from 'react'

/**
 * Um segundo. O gesto não carrega a decisão inteira — ele abre o modal, que
 * enuncia que a ação não se desfaz. São duas travas com papéis distintos: o
 * gesto barra o toque acidental no bolso ou na correria, o modal barra a
 * confirmação irrefletida. Calibrar o gesto como se fosse a única delas
 * cobraria da fila um tempo que a segunda trava já cobra.
 */
export const HOLD_TO_CONFIRM_MS = 1000

/**
 * Classe da superfície que recebe o gesto.
 *
 * `touch-manipulation` e não `touch-none`: mantém a rolagem da página viva.
 * Quem começa um scroll em cima do elemento continua rolando, o navegador
 * dispara `pointercancel` e o gesto se cancela sozinho — que é o certo.
 *
 * Seleção de texto, menu de contexto e realce de toque desligados porque os
 * três aparecem por cima do elemento bem no meio da confirmação.
 */
export const holdSurfaceClassName =
  'cursor-pointer touch-manipulation select-none [-webkit-touch-callout:none] [-webkit-tap-highlight-color:transparent]'

type UseHoldToConfirmOptions = {
  onConfirm: () => void
  /** Fora do estado acionável o elemento não é controle — nem para o leitor de tela. */
  enabled?: boolean
  holdMs?: number
  label?: string
}

const isInsideOptOut = (target: EventTarget | null) =>
  Boolean((target as Element | null)?.closest?.('[data-no-hold]'))

/**
 * Confirmação por gesto sustentado — segurar, e não arrastar.
 *
 * O arrasto depende de movimento contínuo, e é justamente isso que falha com
 * internet ruim e tela cheia de dedo: o quadro trava, o movimento se perde, e a
 * pessoa refaz o gesto na fila. Segurar preserva a intenção deliberada do
 * arrasto sem depender de animação a cada quadro.
 *
 * É um hook e não um botão porque a superfície do gesto é o próprio cartão que
 * o arrasto ocupava — quem já usou não reaprende onde tocar.
 */
export const useHoldToConfirm = ({
  onConfirm,
  enabled = true,
  holdMs = HOLD_TO_CONFIRM_MS,
  label
}: UseHoldToConfirmOptions) => {
  const [isHolding, setIsHolding] = useState(false)
  const completeRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stop = useCallback(() => {
    if (completeRef.current) clearTimeout(completeRef.current)
    completeRef.current = null
    setIsHolding(false)
  }, [])

  // Desmontar no meio do gesto (trocar de aba, fechar a folha) não pode deixar
  // o timer vivo: ele confirmaria um item que já saiu da tela.
  useEffect(() => stop, [stop])

  // Perder a condição de acionável no meio do gesto cancela pelo mesmo motivo.
  useEffect(() => {
    if (!enabled) stop()
  }, [enabled, stop])

  const start = (event: ReactPointerEvent<HTMLElement>) => {
    if (!enabled || completeRef.current) return
    if (isInsideOptOut(event.target)) return

    // Captura o ponteiro: sem isso um tremor de dedo tira o cursor de cima do
    // elemento, dispara `pointerleave` e mata o gesto no meio. Quem segura com
    // o celular estendido não fica firme, e refazer o gesto com a fila andando
    // é o pior desfecho possível. Com captura, só soltar cancela.
    if (event.pointerId != null) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch {
        // Navegador sem captura: segue no comportamento padrão.
      }
    }

    setIsHolding(true)
    // Vibração curta avisa que o gesto pegou, sem exigir que a pessoa olhe a
    // tela. É reforço e nunca o aviso: o Safari do iOS não implementa
    // `vibrate`, então metade do público não recebe nada aqui — quem carrega o
    // progresso é a barra.
    navigator.vibrate?.(12)

    completeRef.current = setTimeout(() => {
      stop()
      navigator.vibrate?.([40, 30, 40])
      onConfirm()
    }, holdMs)
  }

  // `detail` conta cliques de ponteiro: 0 é click sem ponteiro — teclado ou
  // tecnologia assistiva. O click que segue um `pointerup` traz 1 ou mais, e
  // precisa ser ignorado, senão um gesto cancelado no meio confirmaria assim
  // mesmo — o oposto do que o gesto existe para impedir.
  //
  // Não há handler de teclado próprio de propósito: Enter e Espaço sobre um
  // elemento com `role="button"` já viram click nativo, e escutar a tecla
  // também faria a confirmação acontecer duas vezes.
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (event.detail !== 0) return
    if (!enabled) return
    if (isInsideOptOut(event.target)) return

    stop()
    onConfirm()
  }

  const holdProps = enabled
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': label,
        onPointerDown: start,
        onPointerUp: stop,
        onPointerCancel: stop,
        onLostPointerCapture: stop,
        onClick: handleClick,
        onContextMenu: (event: MouseEvent<HTMLElement>) => event.preventDefault()
      }
    : {}

  return { isHolding, holdProps }
}
